import type { ReactNode } from 'react';
import type { PaymentSource, TaxCategoryOption } from '@pet/shared';
import type { AllocationResult } from '../../lib/allocation';
import { formatCentsAsDollars } from '../../lib/money';
import { ACCOUNT_LABELS, BlockReasons, LineFacts, type SpendLine } from './controls';
import styles from '../AddSpending.module.css';

// Review & Save. Everything the save will write, including the method, each line's tax share
// when sales tax was entered, the part not for the properties, and what is owed to the owner.
// Save re-checks the same blocking reasons as the assignment screen.

export interface SummaryScreenProps {
  banner: ReactNode;
  payee: string;
  date: string;
  paymentSource: PaymentSource | '';
  ownerName: string | null;
  paymentMethod: string;
  totalCents: number | null;
  lines: SpendLine[];
  allocation: AllocationResult;
  salesTaxEntered: boolean;
  categories: TaxCategoryOption[];
  labelOf: (propertyId: string) => string;
  categoryLabel: (key: string) => string;
  blockReasons: string[];
  isSaving: boolean;
  onBack: () => void;
  onSave: () => void;
}

export default function SummaryScreen(props: SummaryScreenProps) {
  const { allocation } = props;
  const personalCents = allocation.personal.writtenCents;
  const propertyTotal = allocation.lines.reduce((s, l) => s + l.writtenCents, 0);

  return (
    <main className={styles.container}>
      {props.banner}
      <h2 className={styles.question}>Review &amp; Save</h2>
      <div className={styles.summaryList}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Paid to</span>
          <span className={styles.summaryValue}>{props.payee.trim() || '—'}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Date</span>
          <span className={styles.summaryValue}>{props.date || '—'}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Paid from</span>
          <span className={styles.summaryValue}>
            {props.paymentSource ? ACCOUNT_LABELS[props.paymentSource] : '—'}
          </span>
        </div>
        {props.paymentSource === 'OWNER_FUNDED' && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Owner</span>
            <span className={styles.summaryValue}>{props.ownerName ?? '—'}</span>
          </div>
        )}
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>How it was paid</span>
          <span className={styles.summaryValue}>{props.paymentMethod || 'Not given'}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Total paid</span>
          <span className={styles.summaryValue}>${formatCentsAsDollars(props.totalCents)}</span>
        </div>
      </div>

      <div className={styles.lineCardList}>
        {props.lines.map((line) => {
          const a = allocation.lines.find((l) => l.id === line.id);
          return (
            <div key={line.id} className={styles.lineCard}>
              <span className={styles.lineCardTitle}>
                ${formatCentsAsDollars(a?.writtenCents ?? 0)} · {props.labelOf(line.propertyId)}
              </span>
              <span className={styles.lineCardDetail}>{props.categoryLabel(line.taxCategoryKey)}</span>
              <LineFacts line={line} categories={props.categories} />
              {line.description.trim() && (
                <span className={styles.lineCardDetail}>{line.description.trim()}</span>
              )}
              {props.salesTaxEntered && a && a.preTaxCents !== null && (
                <span className={styles.lineCardDetail}>
                  ${formatCentsAsDollars(a.preTaxCents)} before tax + $
                  {formatCentsAsDollars(a.taxShareCents + a.remainderCents)} tax
                </span>
              )}
            </div>
          );
        })}
      </div>

      {personalCents > 0 && (
        <div className={styles.summaryTotal}>
          <span>Not for the properties</span>
          <span>${formatCentsAsDollars(personalCents)}</span>
        </div>
      )}
      {props.paymentSource === 'OWNER_FUNDED' && (
        <div className={styles.summaryTotal}>
          <span>Owed back to {props.ownerName ?? 'the owner'}</span>
          <span>${formatCentsAsDollars(propertyTotal)}</span>
        </div>
      )}

      <BlockReasons reasons={props.blockReasons} />
      <div className={styles.navRow}>
        <button className={styles.secondaryButton} disabled={props.isSaving} onClick={props.onBack}>
          Back
        </button>
        <button
          className={styles.primaryButton}
          disabled={props.isSaving || props.blockReasons.length > 0}
          onClick={props.onSave}
        >
          {props.isSaving ? 'Saving…' : 'Confirm & Save'}
        </button>
      </div>
    </main>
  );
}
